/**
 * Shared domain types for the "new" pipeline architecture.
 * Pure data + string unions so everything crosses the Worker boundary
 * via structured clone.
 */

export type ColumnType =
  | 'string'
  | 'int'
  | 'float'
  | 'boolean'
  | 'iso-date'
  | 'email'
  | 'uuid'
  | 'url'
  | 'unknown';

/** A single scalar cell. `null` = missing/standardized null. */
export type RowValue = string | null;
/** One table row, positional against `headers`. Ragged rows pad with null. */
export type Row = RowValue[];
/** A chaos/fuzz row keyed by column name (lines up with CSV headers). */
export type ChaosRow = Record<string, string | null>;

export interface Anomaly {
  /** 1-based data-row number (header = row 1, first data row = row 2... as displayed). */
  row: number;
  value: string;
  expected: ColumnType;
  actual: ColumnType;
}

export interface MixedInfo {
  type: ColumnType;
  ratio: number;
}

export interface ColumnSchema {
  name: string;
  key: string;
  type: ColumnType;
  /** Share of populated cells matching the majority type, 0..1. */
  confidence: number;
  anomalyCount: number;
  anomalies: Anomaly[];
  nullCount: number;
  emptyCount: number;
  uniqueCount: number;
  min: string | null;
  max: string | null;
  avg: number | null;
  mixed?: MixedInfo | null;
  /** 'date' vs 'datetime' for iso-date columns. */
  dateKind?: 'date' | 'datetime' | null;
}

export type RepairKey =
  | 'trimWhitespace'
  | 'standardizeNull'
  | 'stripCurrency'
  | 'normalizeDates'
  | 'normalizeHeaders';

export interface RepairConfig {
  trimWhitespace: boolean;
  standardizeNull: boolean;
  stripCurrency: boolean;
  normalizeDates: boolean;
  normalizeHeaders: boolean;
}

export const REPAIR_ORDER: RepairKey[] = [
  'trimWhitespace',
  'standardizeNull',
  'stripCurrency',
  'normalizeDates',
  'normalizeHeaders',
];

export const ALL_REPAIRS: RepairConfig = {
  trimWhitespace: true,
  standardizeNull: true,
  stripCurrency: true,
  normalizeDates: true,
  normalizeHeaders: true,
};

export const NO_REPAIRS: RepairConfig = {
  trimWhitespace: false,
  standardizeNull: false,
  stripCurrency: false,
  normalizeDates: false,
  normalizeHeaders: false,
};

export const DEFAULT_REPAIRS: RepairConfig = { ...ALL_REPAIRS };

export interface RepairReportEntry {
  key: RepairKey;
  applied: boolean;
  changed: number;
  label: string;
}

export type InputFormat = 'auto' | 'csv' | 'json' | 'ndjson';

export interface ParseRequest {
  text: string;
  fileName: string;
  format?: InputFormat | 'auto';
  repairs?: RepairConfig;
  isPro?: boolean;
  delimiter?: string | null;
}

export interface ParseResult {
  fileName: string;
  format: 'csv' | 'json' | 'ndjson';
  headers: string[];
  columns: ColumnSchema[];
  rows: Row[];
  rowCount: number;
  rawRowCount: number;
  truncated: boolean;
  warnings: string[];
  repairs: RepairReportEntry[];
  parseMs: number;
}
